<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import type { FormInstance, FormRules } from "element-plus";
import router from "../../router";
import axios from "axios";
import { ElMessage } from "element-plus";
import { useAuthStore, type userDataInterface } from "../../store/authStore";
import { isCompanyAdminUserType } from "../../utils/authRole";

const store = useAuthStore();
const token = localStorage.getItem("_panda");
axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

interface RuleForm {
  name: string;
  email: string;
  phone_no: string;
}

const formError = ref("");
const results = ref();
const loading = ref(false);
const ruleFormRef = ref<FormInstance>();
const ruleForm = reactive<RuleForm>({ name: "", phone_no: "", email: "" });

const rules = reactive<FormRules<RuleForm>>({
  name: [
    { required: true, message: "Please enter your name", trigger: "blur" },
  ],
  phone_no: [
    {
      required: true,
      message: "Please enter your phone number",
      trigger: "blur",
    },
  ],
  email: [
    {
      required: true,
      message: "Please enter your email address",
      trigger: "blur",
    },
  ],
});

const initializeForm = () => {
  const u = store.getUserData as userDataInterface;
  ruleForm.email = u.user_email || u.email || "";
  ruleForm.phone_no = u.user_phone;
  ruleForm.name = u.user_name;
};
initializeForm();

const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  await formEl.validate((valid) => {
    if (valid) {
      loading.value = true;
      postData();
    }
  });
};

const postData = async () => {
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_ONBOARDING_SERVICE}updateuser?token=${store.getToken}`,
      {
        email: ruleForm.email,
        name: ruleForm.name,
        phone: ruleForm.phone_no,
        company_id: store.getUserData.company_id,
        user_id: store.getUserData.user_id,
        user_type: store.getUserData.user_type,
      },
    );
    loading.value = false;
    if (response.status == 200 && response.data.status) {
      ElMessage({ message: "Profile updated successfully", type: "success" });
      updateStore(ruleForm.name, ruleForm.email, ruleForm.phone_no);
      router.push(
        isCompanyAdminUserType(store.getUserData.user_type)
          ? "/account/team"
          : "/request",
      );
    } else {
      ElMessage({ message: "Update failed, please retry.", type: "error" });
    }
  } catch {
    loading.value = false;
    formError.value = "Something went wrong, please try again.";
  }
};

const updateStore = (name: string, email: string, phone: string) => {
  store.setUserData({
    ...store.getUserData,
    email,
    user_email: email,
    user_name: name,
    user_phone: phone,
  });
};

const companyName = computed(() => store.getUserData?.company_name || "");
const isAdmin = computed(() => isCompanyAdminUserType(store.getUserData?.user_type));

const logwat = () => {
  store.setAuthStatus(false);
  store.$reset();
  localStorage.removeItem("_panda");
  router.push("/login");
};
</script>

<template>
  <div class="ac-card" v-loading="loading">
    <div class="ac-hero">
      <div class="ac-hero-mark is-round">
        {{ ruleForm.name?.charAt(0)?.toUpperCase() || "?" }}
      </div>
      <div class="ac-hero-info">
        <div class="ac-hero-name">{{ ruleForm.name || "Your Name" }}</div>
        <div class="ac-hero-email">{{ ruleForm.email || "your@email.com" }}</div>
        <div class="ac-hero-badges">
          <span class="ac-hero-badge" :class="{ 'ac-hero-badge--admin': isAdmin }">
            {{ isAdmin ? "Admin" : "Member" }}
          </span>
        </div>
      </div>
      <div v-if="companyName" class="ac-hero-aside">
        <div class="ac-hero-aside-label">Company</div>
        <div class="ac-hero-aside-value">{{ companyName }}</div>
      </div>
    </div>

    <div class="ac-card-body">
      <div class="ac-error" v-if="formError">{{ formError }}</div>

      <el-form
        ref="ruleFormRef"
        :model="ruleForm"
        :rules="rules"
        label-width="0px"
        status-icon
      >
        <div class="ac-section-label">Personal details</div>
        <div class="ac-grid">
          <div class="ac-field">
            <label class="ac-label" for="profile-name"
              >Full Name <span class="ac-req">*</span></label
            >
            <el-form-item prop="name" class="ac-fi">
              <input
                id="profile-name"
                v-model="ruleForm.name"
                class="ac-input"
                placeholder="Your full name"
              />
            </el-form-item>
          </div>

          <div class="ac-field">
            <label class="ac-label" for="profile-email"
              >Email Address <span class="ac-req">*</span></label
            >
            <el-form-item prop="email" class="ac-fi">
              <input
                id="profile-email"
                v-model="ruleForm.email"
                class="ac-input"
                type="email"
                placeholder="your@email.com"
              />
            </el-form-item>
          </div>

          <div class="ac-field">
            <label class="ac-label"
              >Phone Number <span class="ac-req">*</span></label
            >
            <el-form-item prop="phone_no" class="ac-fi">
              <MazPhoneNumberInput
                v-model="ruleForm.phone_no"
                defaultCountryCode="KE"
                :only-countries="['KE', 'UG', 'TZ', 'NG', 'ZA']"
                @update="results = $event"
                :success="results?.isValid"
                class="ac-phone"
              />
            </el-form-item>
          </div>
        </div>

        <div class="ac-actions">
          <button
            class="ac-btn"
            @click.prevent="submitForm(ruleFormRef)"
            :disabled="loading"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Save Changes
          </button>
        </div>
      </el-form>
    </div>
  </div>
</template>
